import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Activity,
  Calendar,
  User,
  ArrowUpDown,
  CheckSquare,
  RotateCw,
  FileText,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Database,
  UserPlus,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// ─────────────────────────────────────────────────────────────────────────────
// Activity tab = INTERNAL system events about the account row itself.
// For EXTERNAL touches (campaign sends, replies, meetings, rep engagement
// notes), see AccountEngagementTab. See docs/ACCOUNT_PAGE_GOLD_SHEET.md
// "Activity vs Engagement" for the canonical routing table.
// ─────────────────────────────────────────────────────────────────────────────

interface AccountActivityTabProps {
  accountId: string;
}

interface ActivityRow {
  id: string;
  account_id: string;
  activity_type: string | null;
  description: string | null;
  performed_by: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const DATE_RANGES = [
  { id: "7", label: "Last 7 days", days: 7 },
  { id: "30", label: "Last 30 days", days: 30 },
  { id: "90", label: "Last 90 days", days: 90 },
  { id: "all", label: "All time", days: null },
] as const;

type DateRangeId = (typeof DATE_RANGES)[number]["id"];

function formatActivityType(t: string | null): string {
  if (!t) return "activity";
  return t.replace(/_/g, " ");
}

interface TypeStyle {
  color: string;
  Icon: typeof Activity;
}

const TYPE_STYLES: Record<string, TypeStyle> = {
  tier_changed: { color: "bg-purple-50 text-purple-700 border-purple-200", Icon: ArrowUpDown },
  status_changed: { color: "bg-blue-50 text-blue-700 border-blue-200", Icon: ArrowUpDown },
  reviewed: { color: "bg-green-50 text-green-700 border-green-200", Icon: CheckSquare },
  note_added: { color: "bg-amber-50 text-amber-700 border-amber-200", Icon: FileText },
  note_updated: { color: "bg-amber-50 text-amber-700 border-amber-200", Icon: FileText },
  ai_verdict_recorded: { color: "bg-violet-50 text-violet-700 border-violet-200", Icon: Sparkles },
  ai_verdict_reviewed: { color: "bg-teal-50 text-teal-700 border-teal-200", Icon: ThumbsUp },
  enrichment_run: { color: "bg-indigo-50 text-indigo-700 border-indigo-200", Icon: RotateCw },
  contact_added: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: UserPlus },
};

const DEFAULT_STYLE: TypeStyle = {
  color: "bg-muted text-muted-foreground border-border",
  Icon: Database,
};

function styleFor(t: string | null): TypeStyle {
  if (t && TYPE_STYLES[t]) return TYPE_STYLES[t];
  return DEFAULT_STYLE;
}
// Silence unused import (ThumbsDown is reserved for future reject-verdict styling)
void ThumbsDown;

export function AccountActivityTab({ accountId }: AccountActivityTabProps) {
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeId>("all");

  const { data: rows, isLoading } = useQuery({
    queryKey: ["account-activity-full", accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("account_activities" as any)
        .select("id, account_id, activity_type, description, performed_by, metadata, created_at")
        .eq("account_id", accountId)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return ((data || []) as unknown) as ActivityRow[];
    },
  });

  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    (rows || []).forEach((r) => {
      if (r.activity_type) set.add(r.activity_type);
    });
    return Array.from(set).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    const now = Date.now();
    const rangeDays = DATE_RANGES.find((r) => r.id === dateRange)?.days ?? null;
    const cutoff = rangeDays != null ? now - rangeDays * 24 * 60 * 60 * 1000 : null;
    return rows.filter((r) => {
      if (typeFilter && r.activity_type !== typeFilter) return false;
      if (cutoff != null) {
        const t = new Date(r.created_at).getTime();
        if (t < cutoff) return false;
      }
      return true;
    });
  }, [rows, typeFilter, dateRange]);

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading activity…</div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          <div className="flex gap-1">
            {DATE_RANGES.map((r) => (
              <Button
                key={r.id}
                size="sm"
                variant={dateRange === r.id ? "default" : "outline"}
                onClick={() => setDateRange(r.id)}
                className="h-7 text-xs"
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>

        {availableTypes.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Activity className="h-3.5 w-3.5 text-muted-foreground" />
            <Button
              size="sm"
              variant={typeFilter === null ? "default" : "outline"}
              onClick={() => setTypeFilter(null)}
              className="h-7 text-xs"
            >
              All
            </Button>
            {availableTypes.map((t) => (
              <Button
                key={t}
                size="sm"
                variant={typeFilter === t ? "default" : "outline"}
                onClick={() => setTypeFilter(t)}
                className="h-7 text-xs capitalize"
              >
                {formatActivityType(t)}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      {filteredRows.length === 0 ? (
        <div className="text-center py-10 border rounded-md">
          <Activity className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            {rows?.length ? "No activity matches the current filters." : "No activity recorded yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {filteredRows.map((act) => {
            const dt = new Date(act.created_at);
            const dateLabel = dt.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
            const timeLabel = dt.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
            });
            const style = styleFor(act.activity_type);
            const Icon = style.Icon;
            return (
              <div
                key={act.id}
                className="grid grid-cols-[7rem_12rem_1fr_10rem] gap-3 items-start py-2.5 border-b last:border-0"
              >
                <div className="text-xs text-muted-foreground">
                  <div>{dateLabel}</div>
                  <div className="text-[10px]">{timeLabel}</div>
                </div>
                <div>
                  <Badge
                    variant="outline"
                    className={`text-[11px] capitalize inline-flex items-center gap-1 ${style.color}`}
                  >
                    <Icon className="h-3 w-3" />
                    {formatActivityType(act.activity_type)}
                  </Badge>
                </div>
                <div className="text-sm whitespace-pre-wrap">{act.description || "—"}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                  {act.performed_by && (
                    <>
                      <User className="h-3 w-3" />
                      <span>{act.performed_by}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground">
        Showing {filteredRows.length} of {rows?.length ?? 0} activity records.
      </p>
    </div>
  );
}
